/* ==========================================================================
   search-results.js — The Park Wholesale, search page (sections/main-search.liquid)
   and collection pages (sections/main-collection.liquid). The section to fetch comes
   from data-sr-section-id on [data-sr-section] (default: main-search).
   - Filters live in a drawer opened by the "Filters" button: from the left on desktop,
     from the bottom on phones. Ticking a box updates the results without reloading:
     the section is fetched alone (?section_id=main-search) and the filters, count and
     results are swapped. Focus and open groups stay as they were; screen readers hear
     the new count. Back / Forward work.
   - Closing the drawer after a change: focus back on the button that opened it, the
     heading and count line brought into view, and the count said again.
   - Long lists: "Show all" and a search box.
   - With filters on, the count line also shows the total without filters
     ("5 of 204 products match your filters"), fetched from sections/search-count.liquid.
   - Search box: the clear (x) button.
   - Floating page pill (only with more than one page): shows when the visitor scrolls
     back up (toolbar off screen), hides on scrolling down and near the pagination;
     stays while focus is inside it; focused items stay clear of it (WCAG 2.4.11).
   Without this file the filters show in the page, and the Apply / Sort buttons and
   the pagination still work.
   ========================================================================== */
(function () {
  'use strict';
  if (window.__searchResultsInit) return; // loaded twice: run once
  window.__searchResultsInit = true;

  var reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)');
  var root = document.documentElement;

  var section = document.querySelector('[data-sr-section]');
  if (!section) return;
  var SECTION_ID = section.getAttribute('data-sr-section-id') || 'main-search';
  // Small section with just the product count for the current filters (no product cards, so it
  // comes back sooner): fills the drawer's "Show N products" while the results are loading.
  var COUNT_SECTION = section.getAttribute('data-sr-count-section') || (SECTION_ID === 'main-search' ? 'search-count' : '');

  var drawer = document.querySelector('[data-sf-drawer]');
  var opener = null;
  var busy = null; // AbortController of the running update
  var cache = {};
  var priceTimer = 0;
  var openedOnce = false;
  var lastRender = null;   // promise of the latest results update
  var drawerFrom = null;   // URL when the drawer opened (to tell if filters changed)
  var quietUntil = 0;      // page pill stays hidden until then (during our own scroll)

  // The drawer lives outside the part that gets swapped, so it stays open while results update.
  if (drawer) document.body.appendChild(drawer);

  /* ---------- small helpers ---------- */
  function $(sel, scope) { return (scope || document).querySelector(sel); }
  function $$(sel, scope) { return Array.prototype.slice.call((scope || document).querySelectorAll(sel)); }
  function form() { return $('[data-sf-form]'); }
  function groupsWrap() { return $('[data-sf-groups]'); }
  function status(text) {
    var s = $('[data-sr-status]');
    if (!s) return;
    s.textContent = '';
    window.setTimeout(function () { s.textContent = text; }, 80);
  }

  // Keep the search's own word matching (the page's search box sends options[prefix]=none).
  var prefix = new URLSearchParams(window.location.search).get('options[prefix]');

  function cleanUrl(href) {
    var u = new URL(href, window.location.origin);
    u.searchParams.delete('section_id');
    u.searchParams.delete('page'); // new filters start at page 1
    if (prefix && !u.searchParams.has('options[prefix]')) u.searchParams.set('options[prefix]', prefix);
    return u;
  }
  function urlFromForm() {
    var f = form();
    var u = new URL(f.action, window.location.origin);
    var data = new FormData(f); // includes fields outside the form with form="SearchFilterForm"
    data.forEach(function (value, key) {
      if (typeof value === 'string' && value.trim() === '' && key.indexOf('filter.v.price') === 0) return;
      u.searchParams.append(key, value);
    });
    return cleanUrl(u.toString());
  }

  /* ---------- groups (accordion rows in the drawer) ---------- */
  function setOpen(g, open) {
    var t = $('[data-sf-toggle]', g);
    var p = $('[data-sf-panel]', g);
    if (!t || !p) return;
    t.setAttribute('aria-expanded', open ? 'true' : 'false');
    p.classList.toggle('is-open', open);
  }
  function isOpen(g) { var t = $('[data-sf-toggle]', g); return !!t && t.getAttribute('aria-expanded') === 'true'; }

  function setMore(btn, all) {
    var list = document.getElementById(btn.getAttribute('aria-controls'));
    if (!list) return;
    $$('[data-sf-extra]', list).forEach(function (li) { li.classList.toggle('is-extra-hidden', !all); });
    btn.setAttribute('aria-expanded', all ? 'true' : 'false');
    btn.textContent = all ? btn.getAttribute('data-label-less') : btn.getAttribute('data-label-more');
  }

  function applyFind(input) {
    var list = document.getElementById(input.getAttribute('aria-controls'));
    if (!list) return 0;
    var q = input.value.trim().toLowerCase();
    var panel = list.closest('[data-sf-panel]');
    var more = panel ? $('[data-sf-more]', panel) : null;
    var none = panel ? $('[data-sf-none]', panel) : null;
    var shown = 0;
    $$('.sf-item', list).forEach(function (li) {
      var hit = !q || (li.getAttribute('data-sf-name') || '').indexOf(q) !== -1;
      li.classList.toggle('is-filtered-out', !hit);
      if (hit) shown++;
    });
    list.classList.toggle('is-finding', !!q); // while searching, every match shows
    if (more) more.hidden = !!q;
    if (none) none.hidden = shown > 0;
    return shown;
  }

  function prepareGroups(state) {
    $$('[data-sf-group]').forEach(function (g) {
      setOpen(g, !!(state && state.open.indexOf(g.getAttribute('data-sf-key')) !== -1));
    });
    $$('[data-sf-more]').forEach(function (btn) {
      var key = btn.closest('[data-sf-group]').getAttribute('data-sf-key');
      setMore(btn, !!(state && state.more.indexOf(key) !== -1));
    });
    if (!state) return;
    Object.keys(state.find).forEach(function (id) {
      var input = document.getElementById(id);
      if (input) { input.value = state.find[id]; applyFind(input); }
    });
    Object.keys(state.scroll).forEach(function (id) {
      var el = document.getElementById(id);
      if (el) el.scrollTop = state.scroll[id];
    });
  }

  function captureState() {
    var s = { open: [], more: [], find: {}, scroll: {}, focus: null };
    $$('[data-sf-group]').forEach(function (g) { if (isOpen(g)) s.open.push(g.getAttribute('data-sf-key')); });
    $$('[data-sf-more]').forEach(function (btn) {
      if (btn.getAttribute('aria-expanded') === 'true') s.more.push(btn.closest('[data-sf-group]').getAttribute('data-sf-key'));
    });
    $$('[data-sf-find]').forEach(function (i) { if (i.value) s.find[i.id] = i.value; });
    $$('.sf-list').forEach(function (l) { if (l.scrollTop) s.scroll[l.id] = l.scrollTop; });
    var body = drawer ? $('[data-sf-drawer-body]', drawer) : null;
    if (body && body.scrollTop) { body.id = body.id || 'SearchFilterDrawerBody'; s.scroll[body.id] = body.scrollTop; }
    var a = document.activeElement;
    if (a && a.id && a !== document.body) s.focus = a.id;
    return s;
  }

  // The groups always live in the drawer once this script runs.
  function placeGroups() {
    var g = groupsWrap();
    var body = drawer ? $('[data-sf-drawer-body]', drawer) : null;
    if (!g || !body || body.contains(g)) return;
    body.innerHTML = '';
    body.appendChild(g);
  }

  /* ---------- update the results without reloading ---------- */
  function sectionHtml(url) {
    var u = new URL(url.toString());
    u.searchParams.set('section_id', SECTION_ID);
    var key = u.toString();
    if (cache[key]) return Promise.resolve(cache[key]);
    if (busy) busy.abort();
    busy = new AbortController();
    return fetch(key, { signal: busy.signal, credentials: 'same-origin' }).then(function (r) {
      if (!r.ok) throw new Error('HTTP ' + r.status);
      return r.text();
    }).then(function (html) {
      cache[key] = html;
      return html;
    });
  }

  /* ---------- drawer button: answers at once, the number follows ---------- */
  var counts = {};
  var countFor = '';
  function drawerButton() {
    var foot = drawer ? $('[data-sf-drawer-foot]', drawer) : null;
    return foot ? $('.sf-apply--wide', foot) : null;
  }
  function setButtonCount(n) {
    var btn = drawerButton();
    if (!btn) return;
    btn.classList.remove('is-updating');
    btn.textContent = 'Show ' + n + (n === '1' ? ' product' : ' products');
  }
  function quickCount(url) {
    var btn = drawerButton();
    if (!btn || !drawer.open) return;
    // 1. straight away: "Updating…" (the button still closes the drawer)
    btn.classList.add('is-updating');
    btn.innerHTML = '<span class="sf-spin" aria-hidden="true"></span>Updating…';
    if (!COUNT_SECTION) return;
    // 2. the count alone, usually well before the results
    var u = new URL(url.toString());
    u.searchParams.set('section_id', COUNT_SECTION);
    var key = u.toString();
    countFor = key;
    var got = counts[key] ? Promise.resolve(counts[key]) : fetch(key, { credentials: 'same-origin' }).then(function (r) {
      if (!r.ok) throw new Error('HTTP ' + r.status);
      return r.text();
    }).then(function (html) {
      var el = new DOMParser().parseFromString(html, 'text/html').querySelector('[data-sr-count-total]');
      var n = el ? el.textContent.trim() : '';
      if (n) counts[key] = n;
      return n;
    });
    got.then(function (n) {
      var b = drawerButton();
      // only for the latest change, and only while the button still says "Updating…"
      if (n && key === countFor && b && b.classList.contains('is-updating')) setButtonCount(n);
    }).catch(function () {}); // the results update fills it anyway
  }

  function render(url, opts) {
    opts = opts || {};
    quickCount(url);
    var results = $('[data-sr-results]');
    if (results) results.setAttribute('aria-busy', 'true');
    section.classList.add('is-loading');
    if (drawer) drawer.classList.add('is-loading');

    var job = sectionHtml(url).then(function (html) {
      var doc = new DOMParser().parseFromString(html, 'text/html');
      var next = doc.querySelector('[data-sr-section]');
      if (!next) throw new Error('no section');
      // Links in the fetched HTML must not keep section_id.
      $$('a[href]', next).forEach(function (a) {
        var href = a.getAttribute('href');
        if (href && href.indexOf('section_id=') !== -1) {
          var u = new URL(href, window.location.origin);
          u.searchParams.delete('section_id');
          a.setAttribute('href', u.pathname + u.search);
        }
      });

      var state = captureState();

      // 1. filter groups (in the drawer)
      var oldGroups = groupsWrap();
      var newGroups = next.querySelector('[data-sf-groups]');
      if (oldGroups && newGroups) oldGroups.replaceWith(newGroups);
      else if (oldGroups && !newGroups) oldGroups.remove();

      // 2. "Filters" button badge, count line, results
      var oldOpen = $('.sf-open', section);
      var newOpen = next.querySelector('.sf-open');
      if (oldOpen && newOpen) oldOpen.innerHTML = newOpen.innerHTML;
      var oldCount = $('[data-sr-count]', section);
      var newCount = next.querySelector('[data-sr-count]');
      if (oldCount && newCount) oldCount.innerHTML = newCount.innerHTML;
      var oldPills = $('[data-sr-pills-wrap]', section);
      var newPills = next.querySelector('[data-sr-pills-wrap]');
      if (oldPills && newPills) oldPills.innerHTML = newPills.innerHTML;
      var newResults = next.querySelector('[data-sr-results]');
      var oldResults = $('[data-sr-results]', section);
      if (oldResults && newResults) oldResults.replaceWith(newResults);

      // 3. drawer footer (Clear all, "Show N products")
      var newDrawer = next.querySelector('[data-sf-drawer]');
      if (drawer && newDrawer) {
        var foot = $('[data-sf-drawer-foot]', drawer);
        var newFoot = newDrawer.querySelector('[data-sf-drawer-foot]');
        if (foot && newFoot) foot.innerHTML = newFoot.innerHTML;
      }

      placeGroups();
      prepareGroups(state);
      initQuickBar();

      var nextUrl = url.pathname + url.search;
      if (opts.push !== false && nextUrl !== window.location.pathname + window.location.search) {
        window.history.pushState({ sr: true }, '', nextUrl);
      }

      // Focus back where it was (same checkbox, same search box); else a sensible spot.
      var target = state.focus ? document.getElementById(state.focus) : null;
      if (target && target.getClientRects().length) {
        target.focus({ preventScroll: true });
      } else if (opts.focusCount !== false && (opts.fromLink || (state.focus && !target))) {
        var c = drawer && drawer.open ? $('#SearchFilterDrawerTitle', drawer) : $('[data-sr-count]', section);
        if (c) c.focus({ preventScroll: true });
      }

      // Screen readers: "58 products for hat" / "5 of 204 products match your filters"
      return fillTotal().then(announceCount);
    }).catch(function (err) {
      if (err && err.name === 'AbortError') return;
      window.location.href = url.toString(); // fall back to a normal page load
    }).then(function () {
      section.classList.remove('is-loading');
      if (drawer) drawer.classList.remove('is-loading');
      var r = $('[data-sr-results]');
      if (r) r.removeAttribute('aria-busy');
    });
    lastRender = job;
    return job;
  }

  function announceCount() {
    var countEl = $('[data-sr-count]', section);
    if (!countEl) return;
    var said = countEl.textContent.replace(/\s+/g, ' ').trim();
    var terms = countEl.getAttribute('data-sr-terms');
    if (terms && /products?\b/.test(said) && said.indexOf('match') === -1) said = said.replace(/(products?)\b/, '$1 for ' + terms);
    status(said);
  }

  /* ---------- total without filters ("5 of 204") ---------- */
  var totals = {};
  function fillTotal() {
    var holders = $$('[data-sr-of]', section);
    if (!holders.length) return Promise.resolve();
    var here = new URL(window.location.href);
    var u = new URL(here.pathname, window.location.origin);
    u.searchParams.set('q', here.searchParams.get('q') || '');
    u.searchParams.set('type', 'product');
    if (prefix) u.searchParams.set('options[prefix]', prefix);
    u.searchParams.set('section_id', 'search-count');
    var key = u.toString();
    var got = totals[key] ? Promise.resolve(totals[key]) : fetch(key, { credentials: 'same-origin' }).then(function (r) {
      if (!r.ok) throw new Error('HTTP ' + r.status);
      return r.text();
    }).then(function (html) {
      var el = new DOMParser().parseFromString(html, 'text/html').querySelector('[data-sr-count-total]');
      var n = el ? el.textContent.trim() : '';
      if (n) totals[key] = n;
      return n;
    });
    return got.then(function (n) {
      if (!n) return;
      $$('[data-sr-of]', section).forEach(function (h) {
        var t = $('[data-sr-total]', h);
        if (t) t.textContent = n;
        h.hidden = false;
      });
    }).catch(function () {}); // without it the line still reads "5 products match your filters"
  }

  /* ---------- search box: clear (x) ---------- */
  function syncClear() {
    var input = $('#SearchPageInput', section);
    var clear = $('[data-sr-clear]', section);
    if (input && clear) clear.hidden = input.value === '';
  }

  /* ---------- events (delegated: they keep working after each update) ---------- */
  document.addEventListener('click', function (e) {
    var t = e.target;

    var toggle = t.closest('[data-sf-toggle]');
    if (toggle) {
      var g = toggle.closest('[data-sf-group]');
      setOpen(g, !isOpen(g));
      return;
    }

    var more = t.closest('[data-sf-more]');
    if (more) {
      var all = more.getAttribute('aria-expanded') !== 'true';
      setMore(more, all);
      if (all) {
        var list = document.getElementById(more.getAttribute('aria-controls'));
        var first = list ? $('[data-sf-extra] input', list) : null;
        if (first) first.focus();
      }
      return;
    }

    var link = t.closest('a[data-sf-link]');
    if (link && !e.metaKey && !e.ctrlKey && !e.shiftKey && e.button === 0) {
      e.preventDefault();
      render(cleanUrl(link.href), { fromLink: true });
      return;
    }

    var openBtn = t.closest('[data-sf-open]');
    if (openBtn && drawer) { openDrawer(openBtn); return; }

    if (t.closest('[data-sf-close]')) { closeDrawer(); return; }

    if (t.closest('[data-sr-clear]')) {
      var input = $('#SearchPageInput', section);
      if (input) { input.value = ''; syncClear(); input.focus(); }
    }
  });

  document.addEventListener('change', function (e) {
    var t = e.target;
    if (t.matches('[data-sf-check]') || t.matches('[data-sf-sort]') || t.matches('[data-sf-price]')) {
      window.clearTimeout(priceTimer);
      render(urlFromForm());
    }
  });

  document.addEventListener('input', function (e) {
    var t = e.target;
    if (t.id === 'SearchPageInput') { syncClear(); return; }
    if (t.matches('[data-sf-find]')) {
      var n = applyFind(t);
      window.clearTimeout(t._srTimer);
      t._srTimer = window.setTimeout(function () { status(n === 1 ? '1 match' : n + ' matches'); }, 500);
    } else if (t.matches('[data-sf-price]')) {
      window.clearTimeout(priceTimer);
      priceTimer = window.setTimeout(function () { render(urlFromForm()); }, 900);
    }
  });

  // Enter in a search box shouldn't submit the form.
  document.addEventListener('keydown', function (e) {
    if (e.key === 'Enter' && e.target.matches && e.target.matches('[data-sf-find]')) e.preventDefault();
  });

  document.addEventListener('submit', function (e) {
    if (!e.target.matches('[data-sf-form]')) return;
    e.preventDefault();
    window.clearTimeout(priceTimer);
    render(urlFromForm());
  });

  window.addEventListener('popstate', function () {
    render(new URL(window.location.href), { push: false, focusCount: false });
  });

  /* ---------- drawer ---------- */
  function openDrawer(btn) {
    if (!drawer || typeof drawer.showModal !== 'function' || drawer.open) return;
    placeGroups();
    // First time: open the groups that already have a choice, so it's visible.
    if (!openedOnce) {
      openedOnce = true;
      $$('[data-sf-group]').forEach(function (g) { if ($('.sf-toggle .sf-badge', g)) setOpen(g, true); });
    }
    opener = btn;
    drawerFrom = window.location.search;
    drawer.classList.remove('is-closing');
    drawer.showModal();
    root.classList.add('sf-lock');
    var title = $('#SearchFilterDrawerTitle', drawer);
    if (title) title.focus();
  }
  function closeDrawer() {
    if (!drawer || !drawer.open || drawer.classList.contains('is-closing')) return;
    if (reduceMotion.matches) { drawer.close(); return; }
    drawer.classList.add('is-closing');
    var done = false;
    function finish() {
      if (done) return;
      done = true;
      drawer.removeEventListener('animationend', onEnd);
      drawer.classList.remove('is-closing');
      drawer.close();
    }
    function onEnd(e) { if (e.target === drawer) finish(); }
    drawer.addEventListener('animationend', onEnd);
    window.setTimeout(finish, 350); // in case animationend doesn't fire
  }
  if (drawer) {
    drawer.addEventListener('cancel', function (e) { e.preventDefault(); closeDrawer(); }); // Esc
    drawer.addEventListener('click', function (e) { if (e.target === drawer) closeDrawer(); }); // dimmed area
    drawer.addEventListener('close', function () {
      root.classList.remove('sf-lock');
      // Focus goes back to the button that opened the drawer (the toolbar one if the
      // page pill that opened it was replaced by the update).
      var back = opener && opener.isConnected && opener.getClientRects().length ? opener : $('.sf-open', section);
      if (back && back.getClientRects().length) { try { back.focus({ preventScroll: true }); } catch (e) {} }
      opener = null;
      var changed = drawerFrom !== null && drawerFrom !== window.location.search;
      drawerFrom = null;
      if (!changed) return;
      // Filters changed: once the update is in, bring the heading and count line into
      // view, then say the count again ("50 of 204 products match your filters") after
      // the screen reader has read the focused button.
      Promise.resolve(lastRender).then(function () {
        showResultsTop();
        window.setTimeout(announceCount, 250);
      });
    });
  }

  // Height covered by sticky/fixed bars at the top of the screen (site header, announcement bar).
  function topCover() {
    var cover = 0;
    var hits = document.elementsFromPoint ? document.elementsFromPoint(window.innerWidth / 2, 2) : [];
    hits.forEach(function (el) {
      var pos = getComputedStyle(el).position;
      if (pos !== 'fixed' && pos !== 'sticky') return;
      var r = el.getBoundingClientRect();
      if (r.bottom > cover && r.bottom < window.innerHeight / 2) cover = r.bottom;
    });
    return cover;
  }
  function showResultsTop() {
    var head = $('.sr-head', section) || section;
    var cover = topCover();
    var top = head.getBoundingClientRect().top;
    if (top >= cover && top < window.innerHeight * 0.4) return; // already in view
    quietUntil = Date.now() + 900; // no page pill while this scroll runs
    window.scrollTo({ top: Math.max(0, window.scrollY + top - cover - 12), behavior: reduceMotion.matches ? 'auto' : 'smooth' });
  }

  /* ---------- quick page bar ---------- */
  var quickOff = null;
  function initQuickBar() {
    if (quickOff) { quickOff(); quickOff = null; }
    root.classList.remove('sr-quick-on');
    var bar = $('[data-sr-quick]', section);
    var toolbar = $('[data-sr-toolbar]', section);
    if (!bar || !toolbar || !('IntersectionObserver' in window)) return;
    var pager = $('[data-sr-pager]', section);
    bar.hidden = false;
    var toolbarGone = false, pagerSeen = false, shown = false, goingUp = false;
    var lastY = window.scrollY, travel = 0;

    function cookieBannerOpen() {
      var host = document.querySelector('csm-cookie-consent');
      var region = host && host.shadowRoot && host.shadowRoot.querySelector('[role="region"]');
      if (!region) return false;
      var r = region.getBoundingClientRect();
      return r.width > 0 && r.height > 0;
    }
    // Where the toolbar and the pagination are, read fresh on every check (not only from the
    // observers): after a reload part-way down, or if something else re-draws the toolbar, a
    // remembered state could keep the pill up even at the top of the page.
    function measure() {
      var vh = window.innerHeight || document.documentElement.clientHeight;
      var tb = $('[data-sr-toolbar]', section) || toolbar;
      toolbarGone = tb.getBoundingClientRect().bottom < 0;
      var pg = $('[data-sr-pager]', section) || pager;
      if (pg) {
        var pr = pg.getBoundingClientRect();
        pagerSeen = pr.top < vh && pr.bottom > 0;
      }
    }
    function update() {
      measure();
      var focusedInside = bar.contains(document.activeElement);
      var show = focusedInside || (toolbarGone && !pagerSeen && goingUp && Date.now() > quietUntil && !cookieBannerOpen());
      if (show === shown) return;
      shown = show;
      bar.classList.toggle('is-shown', show);
      root.classList.toggle('sr-quick-on', show);
      if (show) {
        var gap = parseFloat(getComputedStyle(bar).bottom) || 0;
        root.style.setProperty('--sr-quick-h', Math.round(bar.offsetHeight + gap) + 'px');
      }
    }
    // Direction: a few pixels of scrolling one way before it counts (no flicker).
    function onScroll() {
      var y = window.scrollY;
      var d = y - lastY;
      lastY = y;
      if ((d > 0 && travel < 0) || (d < 0 && travel > 0)) travel = 0;
      travel += d;
      if (travel <= -24) goingUp = true;
      else if (travel >= 24) goingUp = false;
      update();
    }
    window.addEventListener('scroll', onScroll, { passive: true });
    window.addEventListener('resize', update, { passive: true });
    window.addEventListener('pageshow', update); // back/forward cache: the page comes back as it was
    function onFocusOut() { window.setTimeout(update, 0); }
    bar.addEventListener('focusout', onFocusOut);
    var o1 = new IntersectionObserver(function () { update(); });
    o1.observe(toolbar);
    var o2 = null;
    if (pager) {
      o2 = new IntersectionObserver(function () { update(); });
      o2.observe(pager);
    }
    var checks = 0;
    var timer = window.setInterval(function () { update(); if (++checks > 120) window.clearInterval(timer); }, 1000);
    function onFocus(e) {
      if (!shown || bar.contains(e.target) || !e.target.getBoundingClientRect) return;
      if (drawer && drawer.contains(e.target)) return;
      var r = e.target.getBoundingClientRect();
      var limit = window.innerHeight - bar.offsetHeight - 12;
      if (r.bottom > limit) window.scrollBy({ top: r.bottom - limit, behavior: reduceMotion.matches ? 'auto' : 'smooth' });
    }
    document.addEventListener('focusin', onFocus);
    quickOff = function () {
      window.removeEventListener('scroll', onScroll);
      window.removeEventListener('resize', update);
      window.removeEventListener('pageshow', update);
      bar.removeEventListener('focusout', onFocusOut);
      o1.disconnect();
      if (o2) o2.disconnect();
      window.clearInterval(timer);
      document.removeEventListener('focusin', onFocus);
    };
  }

  /* ---------- start ---------- */
  placeGroups();
  prepareGroups(null);
  initQuickBar();
  syncClear();
  fillTotal();
  window.history.replaceState({ sr: true }, '', window.location.href);
})();